<template>
  <div class="header-container">
    <div class="blob-container">
      <div v-for="i in 3" :key="i" :class="`blob blob-${i}`"></div>
    </div>
    <div class="reflection-container">
      <div v-for="i in 5" :key="i" :class="`reflection reflection-${i}`"></div>
    </div>
    <div class="header-content" ref="headerContent">
      <h1 class="title">
        <span class="title-word">{{ $t('header.greeting') }}</span>
        <span class="title-word">{{ $t('header.intro') }}</span>
        <span class="title-word">{{ $t('header.name') }}</span>
      </h1>
      <p class="subtitle">{{ $t('header.subtitle') }}</p>
      <div class="cta-container">
        <a href="#projects" class="cta-button" @click.prevent="scrollToSection('projects')">{{ $t('header.viewWork') }}</a>
        <a href="#contact" class="cta-button outline" @click.prevent="scrollToSection('contact')">{{ $t('header.getInTouch') }}</a>
      </div>
    </div>
    <div class="floating-shapes">
      <div class="shape circle"></div>
      <div class="shape square"></div>
      <div class="shape triangle"></div>
    </div>
    <div class="scroll-indicator">
      <span class="scroll-text">{{ $t('header.scrollDown') }}</span>
      <div class="scroll-arrow"></div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

function scrollToSection(sectionId) {
  gsap.to(window, {
    duration: 1.5,
    scrollTo: { y: `#${sectionId}`, offsetY: 50 },
    ease: 'power2.inOut'
  });
}

const headerContent = ref(null);

onMounted(() => {
  // Text animations
  gsap.from(".title-word", {
    opacity: 0,
    y: 100,
    duration: 1,
    stagger: 0.2,
    ease: "back.out(1.7)",
  });

  gsap.from(".subtitle", {
    opacity: 0,
    y: 50,
    duration: 1,
    ease: "power3.out",
    delay: 1,
  });

  // Button animations
  gsap.from(".cta-button", {
    opacity: 1,
    y: 30,
    duration: 4,
    stagger: 0.2,
    ease: "power2.out",
    delay: 1.5,
  });

  // Scroll indicator animation
  gsap.to(".scroll-indicator", {
    y: -20,
    opacity: 1,
    duration: 1,
    ease: "power2.out",
    delay: 2,
  });

  gsap.to(".scroll-arrow", {
    y: 10,
    repeat: -1,
    yoyo: true,
    duration: 0.8,
    ease: "power1.inOut",
  });

  // Blob animations
  gsap.to(".blob", {
    x: "random(-50, 50)",
    y: "random(-30, 30)",
    scale: "random(0.8, 1.2)",
    duration: "random(10, 20)",
    repeat: -1,
    yoyo: true,
    ease: "sine.inOut",
    stagger: {
      each: 0.5,
      from: "random",
    },
  });

  // Reflection animations
  gsap.to(".reflection", {
    x: "random(-100, 100)",
    y: "random(-50, 50)",
    opacity: "random(0.1, 0.3)",
    scale: "random(0.8, 1.5)",
    duration: "random(15, 30)",
    repeat: -1,
    yoyo: true,
    ease: "sine.inOut",
    stagger: {
      each: 0.3,
      from: "random",
    },
  });

  // Floating shapes animation
  gsap.to(".shape", {
    y: "random(-20, 20)",
    x: "random(-20, 20)",
    rotation: "random(-15, 15)",
    duration: "random(3, 5)",
    repeat: -1,
    yoyo: true,
    ease: "sine.inOut",
    stagger: {
      each: 0.2,
      from: "random",
    },
  });

  // Header content fade out on scroll
  ScrollTrigger.create({
    trigger: ".header-container",
    start: "top top",
    end: "bottom top",
    scrub: true,
    onUpdate: (self) => {
      gsap.to(headerContent.value, {
        opacity: 1 - self.progress,
        y: self.progress * 50,
        overwrite: 'auto',
      });
    },
  });
});
</script>

<style scoped>
.header-container {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background-color: #0a0a0a;
  display: flex;
  justify-content: center;
  align-items: center;
}

.blob-container, .reflection-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.3;
}

.blob-1 {
  width: 300px;
  height: 300px;
  background-color: #ff3366;
  top: 20%;
  left: 10%;
}

.blob-2 {
  width: 250px;
  height: 250px;
  background-color: #3366ff;
  bottom: 30%;
  right: 15%;
}

.blob-3 {
  width: 200px;
  height: 200px;
  background-color: #33ff99;
  top: 60%;
  left: 50%;
}

.reflection {
  position: absolute;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
  border-radius: 50%;
}

.reflection-1 { width: 100px; height: 100px; top: 10%; left: 20%; }
.reflection-2 { width: 150px; height: 150px; top: 40%; right: 10%; }
.reflection-3 { width: 80px; height: 80px; bottom: 20%; left: 30%; }
.reflection-4 { width: 120px; height: 120px; top: 70%; right: 25%; }
.reflection-5 { width: 90px; height: 90px; top: 30%; left: 60%; }

.header-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;
}

.title {
  font-size: 5rem;
  margin: 0 0 20px;
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.title-word {
  display: inline-block;
}

.subtitle {
  font-size: 1.8rem;
  margin-top: 1rem;
  margin-bottom: 2rem;
  opacity: 0.8;
}

.cta-container {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
}

.cta-button {
  padding: 12px 24px;
  font-size: 1.2rem;
  text-decoration: none;
  color: white;
  background-color: #ff3366;
  border-radius: 30px;
  transition: all 0.3s ease;
  opacity: 0;
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(255, 51, 102, 0.3);
}

.cta-button.outline {
  background-color: transparent;
  border: 2px solid #ff3366;
}

.cta-button.outline:hover {
  background-color: #ff3366;
}

.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 10;
  opacity: 0;
}

.scroll-text {
  display: block;
  color: white;
  font-size: 1rem;
  margin-bottom: 10px;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.scroll-arrow {
  width: 30px;
  height: 30px;
  border-right: 3px solid white;
  border-bottom: 3px solid white;
  transform: rotate(45deg);
  margin: 0 auto;
}

.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
}

.shape {
  position: absolute;
  opacity: 0.1;
}

.circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: white;
  top: 20%;
  left: 10%;
}

.square {
  width: 40px;
  height: 40px;
  background-color: white;
  top: 70%;
  right: 15%;
}

.triangle {
  width: 0;
  height: 0;
  border-left: 30px solid transparent;
  border-right: 30px solid transparent;
  border-bottom: 52px solid white;
  top: 40%;
  right: 25%;
}

@media (max-width: 768px) {
  .title {
    font-size: 3.5rem;
  }

  .subtitle {
    font-size: 1.4rem;
  }

  .cta-container {
    flex-direction: column;
    align-items: center;
  }

  .cta-button {
    width: 100%;
    max-width: 250px;
  }
}

.title-word,
.subtitle,
.cta-button,
.scroll-text {
  user-select: none;
  caret-color: transparent;
  outline: none;
}
</style>